import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ClipboardCheck, Clock, ListChecks, ShieldCheck, MapPin, History, RotateCcw } from 'lucide-react'
import Button from '@/components/ui/Button'
import SEO from '@/components/common/SEO'
import StickySideBanner from '@/components/common/StickySideBanner'
import LocationSelector from '@/components/recommend/LocationSelector'
import { useChecklistStore } from '@/stores/checklistStore'
import { useAuthStore } from '@/stores/authStore'
import { fetchMyChecklist, type UserChecklist } from '@/lib/api/checklist'
import { useHasMounted } from '@/hooks/useHasMounted'
import { resolveEntryFacilityId } from '@/lib/checklist/entryFacility'

const HIGHLIGHTS = [
  {
    icon: ListChecks,
    title: '60문항 / 10카테고리',
    desc: '법적·인력·비용·의료부터 소통까지, 시설을 고를 때 봐야 할 핵심을 모두 담았어요.',
  },
  {
    icon: Clock,
    title: '필수 항목만 5분',
    desc: '★★★ 23개 필수 항목만 답해도 결과가 나옵니다. 더 자세한 비교가 필요하면 ★★/★ 단계로 계속 진행하세요.',
  },
  {
    icon: ShieldCheck,
    title: '시설별 자동 검증',
    desc: '인증·평가등급·인력·비용 22개 항목은 매일 갱신되는 공공데이터로 자동 비교해요.',
  },
]

export default function ChecklistIntroPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { reset, userLocation, setUserLocation, hydrateFromServer, setEntryFacilityId } = useChecklistStore()
  const { isAuthenticated } = useAuthStore()
  // SSR 대상 페이지(ssr-spec §15.4) — persist 스토어의 위치 값은 서버에 없으므로
  // 마운트 후에만 반영해 하이드레이션 불일치를 막는다.
  const hasMounted = useHasMounted()

  // 시설 상세에서 진입한 경우 — 시설 id 를 store 컨텍스트로 저장.
  // 결과 페이지에서 이 시설의 단일 자동 검증 카드를 노출하는 데 사용된다.
  // router state 우선, 옛 ?facilityId= 쿼리는 하위 호환 (ssr-spec §17.2.2).
  // 쿼리로 들어온 URL 을 /checklist 로 replace 하지 않는다 — utm 이 GA 집계 전에 지워질 수 있다.
  useEffect(() => {
    const entryFacilityId = resolveEntryFacilityId(location.state, location.search)
    if (entryFacilityId != null) {
      setEntryFacilityId(entryFacilityId)
    }
  }, [location.state, location.search, setEntryFacilityId])

  const [savedChecklist, setSavedChecklist] = useState<UserChecklist | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      setSavedChecklist(null)
      return
    }
    let cancelled = false
    fetchMyChecklist()
      .then((data) => {
        if (cancelled) return
        if (data && data.answeredCount > 0) setSavedChecklist(data)
        else setSavedChecklist(null)
      })
      .catch((e) => {
        if (cancelled) return
        console.error('failed to fetch saved checklist', e)
        setSavedChecklist(null)
      })
    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  const handleStart = () => {
    // reset()이 entryFacilityId(시설 컨텍스트)를 지우므로, 시작 시점의 진입 정보에서
    // 시설 id 를 다시 읽어 복원한다. 이게 없으면 결과 페이지의 '시설 자동 검증
    // 결과' 카드가 절대 뜨지 않는다(시설 상세→체크리스트 진입 시 컨텍스트 유실).
    // 마운트 useEffect 와 같은 소스(resolveEntryFacilityId)를 봐야 한다 (ssr-spec §17.2.2).
    const entryFacilityId = resolveEntryFacilityId(location.state, location.search)
    reset()
    if (entryFacilityId != null) {
      setEntryFacilityId(entryFacilityId)
    }
    navigate('/checklist/step/1')
  }

  const handleResumeView = () => {
    if (!savedChecklist) return
    hydrateFromServer({
      answers: savedChecklist.answers,
      completedAt: savedChecklist.completedAt,
      targetFacilityIds: savedChecklist.targetFacilityIds,
    })
    navigate('/checklist/result')
  }

  const handleResumeEdit = () => {
    if (!savedChecklist) return
    hydrateFromServer({
      answers: savedChecklist.answers,
      completedAt: null,
      targetFacilityIds: savedChecklist.targetFacilityIds,
    })
    navigate('/checklist/step/1')
  }

  // history.key === 'default' → 직접 URL 진입(이전 페이지 없음). 그 외에는 push 로 들어왔으므로 뒤로 이동.
  const handleLater = () => {
    if (location.key === 'default') {
      navigate('/')
    } else {
      navigate(-1)
    }
  }

  return (
    <>
      {/* 좌우 세로배너 160x600 — max-w-2xl. 336+160+48(여백) = 544 */}
      <StickySideBanner slotKey="checklist_side_left" side="left" topPx={96} edgeOffsetPx={544} breakpoint="xl" />
      <StickySideBanner slotKey="checklist_side_right" side="right" topPx={96} edgeOffsetPx={544} breakpoint="xl" />
      <SEO
        title="요양시설 선택 체크리스트 60문항"
        description="요양원·실버타운 선택 전 꼭 확인해야 할 60가지 항목. 법적 인증부터 인력·비용·의료체계까지 한 번에 점검하세요."
        url="/checklist"
      />

      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-cb-primary/10">
            <ClipboardCheck className="h-8 w-8 text-cb-primary" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            우리 부모님 시설, <br className="sm:hidden" />
            <span className="text-cb-primary">60문항</span>으로 점검하세요
          </h1>
          <p className="mt-3 text-sm text-gray-600 sm:text-base">
            요양시설 선택은 한 번 결정하면 바꾸기 어려운 큰 결정이에요. <br />
            중요한 항목을 놓치지 않도록 도와드릴게요.
          </p>
        </div>

        {/* 이전 응답 안내 — 로그인 사용자가 저장본을 가진 경우만 */}
        {savedChecklist && (
          <div className="mb-6 rounded-xl border border-cb-primary/30 bg-cb-primary/5 p-4 shadow-sm">
            <div className="mb-3 flex items-start gap-2">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-cb-primary/10">
                <History className="h-4 w-4 text-cb-primary" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">이어서 보시겠어요?</h3>
                <p className="mt-0.5 text-xs leading-relaxed text-gray-600">
                  저장된 응답 {savedChecklist.answeredCount}개가 있어요
                  {savedChecklist.completedAt && (
                    <>
                      {' · '}
                      {new Date(savedChecklist.completedAt).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}{' '}
                      완료
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleResumeView}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-cb-primary py-2.5 text-sm font-semibold text-white hover:bg-cb-primary/90"
              >
                <ClipboardCheck className="h-4 w-4" />
                결과 보기
              </button>
              <button
                type="button"
                onClick={handleResumeEdit}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-cb-primary/30 bg-white py-2.5 text-sm font-semibold text-cb-primary hover:bg-cb-primary/5"
              >
                <RotateCcw className="h-4 w-4" />
                다시 응답
              </button>
            </div>
          </div>
        )}

        {/* 위치 입력 (선택) — spec §4.5 LOC_01 동적 평가에 활용. plan §5.2 */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-start gap-2">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-cb-primary/10">
              <MapPin className="h-4 w-4 text-cb-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                내 위치를 알려주세요 <span className="text-xs font-normal text-gray-400">(선택)</span>
              </h3>
              <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                위치를 설정하면 가까운 시설부터 추천해드려요. 입력 안 해도 진행 가능해요.
              </p>
            </div>
          </div>
          <LocationSelector
            locationType={hasMounted ? (userLocation?.type ?? null) : null}
            addressText={hasMounted ? (userLocation?.addressText ?? null) : null}
            onSetLocation={(data) =>
              setUserLocation({
                type: data.type,
                latitude: data.latitude,
                longitude: data.longitude,
                addressText: data.addressText,
              })
            }
            onClearLocation={() => setUserLocation(null)}
          />
        </div>

        <div className="space-y-3">
          {HIGHLIGHTS.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-cb-primary/10">
                  <Icon className="h-5 w-5 text-cb-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-gray-600">{desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-xl bg-amber-50 p-4 text-xs text-amber-900">
          <p className="leading-relaxed">
            ℹ️ 일부 항목은 시설이 아직 등록하지 않은 정보예요. 결과 화면에서{' '}
            <strong>‘정보 등록 전’</strong>으로 표시됩니다.
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <Button onClick={handleStart} className="w-full" size="lg">
            체크리스트 시작
          </Button>
          <button
            type="button"
            onClick={handleLater}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            나중에 할게요
          </button>
        </div>
      </div>
    </>
  )
}
